import type { ComponentType, FC, ReactNode } from 'react'

import Image from 'next/image'
import { Clock3, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SafetyNoteProps {
    title: string
    body: string
    tag: string
    icon: ComponentType<{ className?: string }>
    align: 'left' | 'right'
    className?: string
}

const SafetyNote: FC<SafetyNoteProps> = ({ title, body, tag, icon: Icon, align, className }): ReactNode => {
    return (
        <div
            data-reveal={align === 'right' ? 'left' : 'right'}
            className={cn('reveal reveal-d3 max-w-sm text-center', align === 'right' ? 'lg:text-right' : 'lg:text-left', className)}
        >
            <h3 className='font-heading text-[1.9rem] leading-[1.05] tracking-[-0.01em] text-foreground'>
                {title}
            </h3>

            <p className='mt-3 text-[15px] leading-relaxed text-muted-foreground'>
                {body}
            </p>

            <p className='mt-5 inline-flex items-center gap-2 rounded-full border border-border bg-paper/70 px-3 py-1.5 text-[13px] font-medium text-foreground/80'>
                <Icon className='size-4 text-leaf' />
                {tag}
            </p>
        </div>
    )
}

const Postmark: FC = (): ReactNode => {
    return (
        <svg viewBox='0 0 200 120' aria-hidden='true' data-reveal='press' className='reveal reveal-d6 pointer-events-none absolute -top-8 -right-8 w-36 rotate-[8deg] text-primary/60 sm:-right-10 sm:w-40'>
            <defs>
                <path id='postmark-ring' d='M 60 60 m -38 0 a 38 38 0 1 1 76 0 a 38 38 0 1 1 -76 0' />
            </defs>

            <circle cx='60' cy='60' r='50' fill='none' stroke='currentColor' strokeWidth='1.4' />
            <circle cx='60' cy='60' r='30' fill='none' stroke='currentColor' strokeWidth='1' />

            <text fill='currentColor' fontSize='9.5' letterSpacing='2.6' style={{ fontFamily: 'var(--font-app-sans, sans-serif)' }}>
                <textPath href='#postmark-ring'>
                    OFFER · TO · KEYS · CLOSEWELL · OFFER · TO · KEYS ·
                </textPath>
            </text>

            <text x='60' y='57' textAnchor='middle' fill='currentColor' fontSize='11' style={{ fontFamily: 'var(--font-app-heading, serif)', fontStyle: 'italic' }}>
                Closed
            </text>

            <text x='60' y='70' textAnchor='middle' fill='currentColor' fontSize='7.5' letterSpacing='1.2' style={{ fontFamily: 'var(--font-app-sans, sans-serif)' }}>
                NOV 14
            </text>

            <path d='M114 44 q 10 -6 20 0 t 20 0 t 20 0 t 20 0' fill='none' stroke='currentColor' strokeWidth='1.4' />
            <path d='M114 60 q 10 -6 20 0 t 20 0 t 20 0 t 20 0' fill='none' stroke='currentColor' strokeWidth='1.4' />
            <path d='M114 76 q 10 -6 20 0 t 20 0 t 20 0 t 20 0' fill='none' stroke='currentColor' strokeWidth='1.4' />
        </svg>
    )
}

export const SafetySection: FC = (): ReactNode => {
    return (
        <section id='safety' aria-labelledby='safety-title' className='overflow-x-clip px-5 pt-28 pb-8 sm:pt-36'>
            <div className='mx-auto max-w-3xl text-center'>
                <Image
                    src='/images/wren.png'
                    alt=''
                    width={2048}
                    height={2048}
                    sizes='136px'
                    className='float-soft mx-auto -mb-3 size-[136px] mix-blend-multiply'
                />

                <h2 id='safety-title' className='reveal font-heading text-[clamp(3rem,6.4vw,5.25rem)] leading-[0.95] tracking-[-0.03em] text-foreground'>
                    Quiet automation,
                    <span className='block italic'>your final say</span>
                </h2>

                <p className='reveal reveal-d1 mx-auto mt-6 max-w-[33rem] text-[17px] leading-[1.5] text-muted-foreground sm:text-lg'>
                    Closewell drafts, schedules and reminds. Nothing reaches a client, a lender or another agent until someone on your team says yes.
                </p>
            </div>

            <div className='mx-auto mt-16 grid max-w-6xl items-center justify-items-center gap-12 sm:mt-20 lg:grid-cols-[1fr_auto_1fr] lg:gap-14'>
                <SafetyNote
                    title='You approve every message'
                    body='Closewell is a careful assistant, not an autopilot. Drafts wait in your queue until you send them, and every step is logged on the deal.'
                    tag='Your clients stay in good hands'
                    icon={ShieldCheck}
                    align='right'
                    className='order-2 lg:order-1 lg:justify-self-end'
                />

                <div className='relative order-1 lg:order-2'>
                    <div data-reveal='stamp' className='reveal reveal-d1'>
                        <div className='-rotate-[5deg] drop-shadow-[0_22px_30px_rgba(60,48,24,0.28)] transition-[rotate,scale] duration-700 ease-out hover:-rotate-[2deg] hover:scale-[1.03] motion-reduce:hover:rotate-[-5deg] motion-reduce:hover:scale-100'>
                            <div className='stamp-edge'>
                                <Image
                                    src='/images/keys-handoff.jpg'
                                    alt='Painting of one hand passing a ring of house keys to another over a porch railing, in front of a white cottage with a blue door'
                                    width={2304}
                                    height={1856}
                                    sizes='(min-width: 640px) 340px, 260px'
                                    className='block h-auto w-[260px] sm:w-[340px]'
                                />
                            </div>
                        </div>
                    </div>

                    <Postmark />
                </div>

                <SafetyNote
                    title='Evenings back, deals still moving'
                    body='Reminders, scheduling and status updates happen while you are out with clients, so the inbox is quiet when you get home.'
                    tag='About 6 hours back per deal'
                    icon={Clock3}
                    align='left'
                    className='order-3 lg:justify-self-start'
                />
            </div>
        </section>
    )
}
